import { useState, useRef, useCallback, useId } from 'react';
import type { DistanceType } from '../../types';
import { formatDistance } from '../../utils/distance';
import { usePopoverDismiss } from '../../hooks/usePopoverDismiss';
import './DistanceFilterPopover.css';

interface DistanceFilterPopoverProps {
  selectedDistances: DistanceType[];
  onDistancesChange: (distances: DistanceType[]) => void;
}

const DISTANCE_OPTIONS: { value: DistanceType; sub: string }[] = [
  { value: '5K', sub: '5 km' },
  { value: '10K', sub: '10 km' },
  { value: 'half', sub: '21.1 km · 하프' },
  { value: '32K', sub: '32 km' },
  { value: 'full', sub: '42.2 km · 풀' },
  { value: 'ultra', sub: '50 km+ · 울트라' },
  { value: 'trail', sub: '트레일' },
];

export function DistanceFilterPopover({
  selectedDistances,
  onDistancesChange,
}: DistanceFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  usePopoverDismiss(open, wrapperRef, useCallback(() => setOpen(false), []));

  const toggle = (d: DistanceType) => {
    if (selectedDistances.includes(d)) {
      onDistancesChange(selectedDistances.filter((x) => x !== d));
    } else {
      onDistancesChange([...selectedDistances, d]);
    }
  };

  const clear = () => onDistancesChange([]);

  const count = selectedDistances.length;

  return (
    <div className="distance-popover-wrapper" ref={wrapperRef}>
      <button
        className={`filter-trigger ${count > 0 ? 'active' : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={popoverId}
      >
        <span className="filter-trigger-label">거리</span>
        {count > 0 && <span className="filter-trigger-count">{count}</span>}
        <span className={`filter-trigger-chevron ${open ? 'open' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="filter-popover" id={popoverId} role="dialog" aria-label="거리 필터">
          <div className="filter-popover-header">
            <span className="filter-popover-title eyebrow">거리 선택</span>
            {count > 0 && (
              <button className="filter-popover-clear" onClick={clear}>
                초기화
              </button>
            )}
          </div>
          <ul className="filter-popover-list">
            {DISTANCE_OPTIONS.map((opt) => {
              const id = `${popoverId}-${opt.value}`;
              const checked = selectedDistances.includes(opt.value);
              return (
                <li key={opt.value} className="filter-popover-item">
                  <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt.value)}
                  />
                  <label htmlFor={id} className="filter-popover-label">
                    <span className="filter-popover-primary display">
                      {formatDistance(opt.value)}
                    </span>
                    <span className="filter-popover-sub">{opt.sub}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
